import { useEffect, useState } from "react";
import { Link } from "react-router";
import { Loader2, RefreshCw } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { SearchInput } from "@/components/ui/search-input";
import { FilterToolbar } from "@/components/ui/filter-toolbar";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { EmptyState, ErrorState, LoadingState } from "@/components/ui/states";
import { ListPagination } from "@/components/ui/list-pagination";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useToast } from "@/hooks/use-toast";
import { formatApiError } from "@/lib/api-error";
import { formatDateTime } from "@/utils/dateUtils";
import {
  useOmieClientes,
  useOmieCopiarParaCadastro,
  useOmieDesvincular,
  useOmieSincronizarClientes,
  useOmieStatus,
  useOmieConferirVinculo,
  useOmieVincular,
} from "@/hooks/use-finance-omie";
import { OmieVinculoPickerDialog } from "@/components/finance/OmieVinculoPickerDialog";
import type {
  OmieCliente,
  OmieOrigemSugestao,
  OmieVinculoFiltro,
} from "@/types/finance";

const ORIGEM_LABELS: Record<OmieOrigemSugestao, string> = {
  email: "Email",
  cnpj: "CNPJ",
  nome: "Nome",
};

const VINCULO_FILTROS: OmieVinculoFiltro[] = ["a_conferir", "pendente", "sugerido", "confirmado", "todos"];

const VINCULO_LABELS: Record<OmieVinculoFiltro, string> = {
  a_conferir: "Vinculados pelo sistema",
  pendente: "Pendentes",
  sugerido: "Sugeridos",
  confirmado: "Confirmados",
  todos: "Todos",
};

const PAGE_SIZE = 24;

export function CadastrosOmieTab() {
  const { toast } = useToast();
  const [vinculo, setVinculo] = useState<OmieVinculoFiltro>("a_conferir");
  const [termoBusca, setTermoBusca] = useState("");
  const [termoDebounced, setTermoDebounced] = useState("");
  const [pagina, setPagina] = useState(1);
  const [clienteParaVincular, setClienteParaVincular] = useState<OmieCliente | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setTermoDebounced(termoBusca);
      setPagina(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [termoBusca]);

  const statusQuery = useOmieStatus();
  const sincronizar = useOmieSincronizarClientes();
  const clientesQuery = useOmieClientes({
    vinculo,
    q: termoDebounced,
    page: pagina,
    page_size: PAGE_SIZE,
  });
  const vincular = useOmieVincular();
  const conferir = useOmieConferirVinculo();
  const desvincular = useOmieDesvincular();
  const copiarParaCadastro = useOmieCopiarParaCadastro();

  const empresas = statusQuery.data?.empresas ?? [];
  const algumaEmpresaConfigurada = empresas.some((empresa) => empresa.credenciais_configuradas);
  const resumo = clientesQuery.data?.resumo;
  const itens = clientesQuery.data?.items ?? [];
  const total = clientesQuery.data?.total ?? 0;
  const totalPaginas = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const handleSincronizar = async () => {
    try {
      const resultado = await sincronizar.mutateAsync();
      const totalNovos = resultado.empresas.reduce((soma, empresa) => soma + empresa.novos, 0);
      const totalAtualizados = resultado.empresas.reduce(
        (soma, empresa) => soma + empresa.atualizados,
        0,
      );
      toast({
        title: "Sincronização concluída",
        description: `${totalNovos} novos, ${totalAtualizados} atualizados, ${resultado.sugestoes_novas} novas sugestões de vínculo.`,
      });
    } catch (err) {
      const formatted = formatApiError(err);
      toast({ variant: "destructive", title: formatted.title, description: formatted.detail });
    }
  };

  const handleConfirmarSugestao = async (cliente: OmieCliente) => {
    if (!cliente.sugestao) return;
    const origem = cliente.sugestao.origem === "nome" ? "nome_confirmado" : cliente.sugestao.origem;
    try {
      await vincular.mutateAsync({
        clienteId: cliente.id,
        body: {
          origem,
          ...(cliente.sugestao.agencia_id
            ? { agencia_id: cliente.sugestao.agencia_id }
            : { freelancer_id: cliente.sugestao.freelancer_id ?? undefined }),
        },
      });
      toast({ title: "Vínculo confirmado" });
    } catch (err) {
      const formatted = formatApiError(err);
      toast({ variant: "destructive", title: formatted.title, description: formatted.detail });
    }
  };

  const handleConferir = async (cliente: OmieCliente) => {
    try {
      await conferir.mutateAsync(cliente.id);
      toast({ title: "Vínculo conferido" });
    } catch (err) {
      const formatted = formatApiError(err);
      toast({ variant: "destructive", title: formatted.title, description: formatted.detail });
    }
  };

  const handleDesfazer = async (cliente: OmieCliente) => {
    try {
      await desvincular.mutateAsync(cliente.id);
      toast({ title: "Vínculo desfeito" });
    } catch (err) {
      const formatted = formatApiError(err);
      toast({ variant: "destructive", title: formatted.title, description: formatted.detail });
    }
  };

  const handleCopiarParaCadastro = async (cliente: OmieCliente) => {
    try {
      const resultado = await copiarParaCadastro.mutateAsync({
        clienteId: cliente.id,
        body: { campos: ["razao_social", "cnpj"], sobrescrever: false },
      });
      const atualizadosTexto =
        resultado.atualizados.length > 0
          ? `Atualizado: ${resultado.atualizados.join(", ")}.`
          : "Nenhum campo foi atualizado.";
      const ignoradosTexto =
        resultado.ignorados.length > 0
          ? ` Ignorado: ${resultado.ignorados
              .map((item) => `${item.campo} (${item.motivo})`)
              .join(", ")}.`
          : "";
      toast({ title: "Cadastro atualizado", description: atualizadosTexto + ignoradosTexto });
    } catch (err) {
      const formatted = formatApiError(err);
      toast({ variant: "destructive", title: formatted.title, description: formatted.detail });
    }
  };

  return (
    <div className="space-y-5">
      <div className="grid gap-3 sm:grid-cols-2">
        {statusQuery.isLoading && <LoadingState variant="list" count={2} />}
        {empresas.map((empresa) => (
          <Card key={empresa.chave}>
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center justify-between gap-2 text-sm font-medium">
                {empresa.nome}
                <Badge variant={empresa.credenciais_configuradas ? "secondary" : "outline"}>
                  {empresa.credenciais_configuradas ? "Credenciais configuradas" : "Sem credenciais"}
                </Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-1 text-sm text-muted-foreground">
              <p>
                {empresa.vinculados} vinculados de {empresa.total_clientes} clientes
              </p>
              <p>
                {empresa.ultimo_sync
                  ? `Última sincronização em ${formatDateTime(empresa.ultimo_sync)}`
                  : "Nunca sincronizado"}
              </p>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="flex justify-end">
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <span>
                <Button
                  onClick={handleSincronizar}
                  disabled={sincronizar.isPending || !algumaEmpresaConfigurada}
                  className="gap-1.5"
                >
                  {sincronizar.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <RefreshCw className="h-4 w-4" />
                  )}
                  Sincronizar com o Omie
                </Button>
              </span>
            </TooltipTrigger>
            {!algumaEmpresaConfigurada && (
              <TooltipContent>Nenhuma empresa tem credenciais do Omie configuradas.</TooltipContent>
            )}
          </Tooltip>
        </TooltipProvider>
      </div>

      <div className="space-y-3">
        <FilterToolbar>
          <SearchInput
            value={termoBusca}
            onChange={setTermoBusca}
            placeholder="Buscar por razão social, CNPJ ou email..."
          />
        </FilterToolbar>

        <div className="flex flex-wrap gap-1.5">
          {VINCULO_FILTROS.map((chave) => {
            const contagem =
              chave === "todos"
                ? resumo?.total
                : chave === "a_conferir"
                  ? resumo?.a_conferir
                  : chave === "confirmado"
                  ? resumo?.confirmados
                  : chave === "sugerido"
                    ? resumo?.sugeridos
                    : resumo?.pendentes;
            const ativo = vinculo === chave;
            return (
              <Button
                key={chave}
                type="button"
                size="sm"
                variant={ativo ? "default" : "outline"}
                onClick={() => {
                  setVinculo(chave);
                  setPagina(1);
                }}
                className="gap-1.5"
              >
                {VINCULO_LABELS[chave]}
                {contagem != null && (
                  <Badge variant={ativo ? "secondary" : "outline"} className="ml-0.5">
                    {contagem}
                  </Badge>
                )}
              </Button>
            );
          })}
        </div>
      </div>

      {clientesQuery.isLoading ? (
        <LoadingState variant="list" count={6} />
      ) : clientesQuery.isError ? (
        <ErrorState onRetry={() => clientesQuery.refetch()} />
      ) : itens.length === 0 ? (
        <EmptyState
          title="Nenhum cadastro encontrado"
          description="Ajuste os filtros ou sincronize com o Omie."
        />
      ) : (
        <>
          <div className="overflow-x-auto rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Razão social</TableHead>
                  <TableHead>Documento</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Empresa</TableHead>
                  <TableHead>Vinculado a</TableHead>
                  <TableHead className="text-right">Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {itens.map((cliente) => (
                  <LinhaClienteOmie
                    key={cliente.id}
                    cliente={cliente}
                    onConfirmarSugestao={() => handleConfirmarSugestao(cliente)}
                    onBuscarOutro={() => setClienteParaVincular(cliente)}
                    onVincular={() => setClienteParaVincular(cliente)}
                    onDesfazer={() => handleDesfazer(cliente)}
                    onConferir={() => handleConferir(cliente)}
                    conferindo={conferir.isPending}
                    onCopiarParaCadastro={() => handleCopiarParaCadastro(cliente)}
                    confirmando={vincular.isPending}
                    desfazendo={desvincular.isPending}
                    copiando={copiarParaCadastro.isPending}
                  />
                ))}
              </TableBody>
            </Table>
          </div>
          <ListPagination currentPage={pagina} totalPages={totalPaginas} onPageChange={setPagina} />
        </>
      )}

      {clienteParaVincular && (
        <OmieVinculoPickerDialog
          open={!!clienteParaVincular}
          onOpenChange={(open) => !open && setClienteParaVincular(null)}
          cliente={clienteParaVincular}
        />
      )}
    </div>
  );
}

interface LinhaClienteOmieProps {
  cliente: OmieCliente;
  onConfirmarSugestao: () => void;
  onBuscarOutro: () => void;
  onVincular: () => void;
  onDesfazer: () => void;
  onConferir: () => void;
  conferindo: boolean;
  onCopiarParaCadastro: () => void;
  confirmando: boolean;
  desfazendo: boolean;
  copiando: boolean;
}

function LinhaClienteOmie({
  cliente,
  onConfirmarSugestao,
  onBuscarOutro,
  onVincular,
  onDesfazer,
  onConferir,
  conferindo,
  onCopiarParaCadastro,
  confirmando,
  desfazendo,
  copiando,
}: LinhaClienteOmieProps) {
  const cadastroId = cliente.vinculo?.agencia_id || cliente.vinculo?.freelancer_id;
  const fichaPath = cliente.vinculo?.agencia_id ? `/agencias/${cadastroId}` : `/freelancers/${cadastroId}`;

  return (
    <TableRow>
      <TableCell>
        <div className="font-medium">{cliente.razao_social}</div>
        {cliente.nome_fantasia && (
          <div className="text-xs text-muted-foreground">{cliente.nome_fantasia}</div>
        )}
      </TableCell>
      <TableCell className="text-sm">{cliente.cnpj_cpf || "-"}</TableCell>
      <TableCell className="text-sm">{cliente.email || "-"}</TableCell>
      <TableCell>
        <Badge variant="outline">{cliente.omie_empresa.nome}</Badge>
      </TableCell>
      <TableCell className="min-w-[220px]">
        {cliente.vinculo ? (
          <div className="space-y-1">
            <p className="text-xs text-muted-foreground">
              {cliente.vinculo.agencia_id ? "Agência" : "Freelancer"}
            </p>
            {cadastroId ? (
              <Link to={fichaPath} className="text-sm font-medium text-primary hover:underline">
                {cliente.vinculo.nome}
              </Link>
            ) : (
              <span className="text-sm font-medium">{cliente.vinculo.nome}</span>
            )}
            {cliente.vinculo.origem === "inferido" && (
              <div
                className={cn(
                  "rounded-md border px-2 py-1.5 text-xs",
                  cliente.vinculo.conferido
                    ? "border-emerald-200 bg-emerald-50/60 text-emerald-900"
                    : "border-amber-200 bg-amber-50 text-amber-900",
                )}
              >
                <p className="font-medium">
                  {cliente.vinculo.conferido ? "Vinculado pelo sistema e conferido" : "Vinculado pelo sistema, confira"}
                </p>
                {cliente.vinculo.detalhe && <p className="mt-0.5 leading-snug">{cliente.vinculo.detalhe}</p>}
              </div>
            )}
            <div className="flex flex-wrap items-center gap-1.5">
              {cliente.vinculo.origem === "inferido" && !cliente.vinculo.conferido && (
                <>
                  <Button
                    type="button"
                    variant="link"
                    size="sm"
                    className="h-auto p-0 text-xs font-medium"
                    disabled={conferindo}
                    onClick={onConferir}
                  >
                    Está certo
                  </Button>
                  <span className="text-muted-foreground">·</span>
                </>
              )}
              <Button
                type="button"
                variant="link"
                size="sm"
                className="h-auto p-0 text-xs"
                disabled={desfazendo}
                onClick={onDesfazer}
              >
                Desfazer
              </Button>
              <span className="text-muted-foreground">·</span>
              <Button
                type="button"
                variant="link"
                size="sm"
                className="h-auto p-0 text-xs"
                disabled={copiando}
                onClick={onCopiarParaCadastro}
              >
                Copiar razão social e CNPJ para o cadastro
              </Button>
            </div>
          </div>
        ) : cliente.observacao && !cliente.sugestao ? (
          <div className="space-y-1.5">
            <p className="rounded-md border border-amber-200 bg-amber-50 px-2 py-1.5 text-xs leading-snug text-amber-900">
              {cliente.observacao}
            </p>
            <Button type="button" variant="link" size="sm" className="h-auto p-0 text-xs" onClick={onVincular}>
              Vincular mesmo assim
            </Button>
          </div>
        ) : cliente.sugestao ? (
          <div className="space-y-1.5">
            <p className="text-xs text-muted-foreground">
              Sugestão: {cliente.sugestao.agencia_id ? "agência" : "freelancer"}
            </p>
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="text-sm font-medium">{cliente.sugestao.nome}</span>
              <Badge variant="secondary" className="text-xs">
                {ORIGEM_LABELS[cliente.sugestao.origem]} · {Math.round(cliente.sugestao.score * 100)}%
              </Badge>
            </div>
            <div className="flex gap-1.5">
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={confirmando}
                onClick={onConfirmarSugestao}
              >
                Confirmar
              </Button>
              <Button type="button" size="sm" variant="ghost" onClick={onBuscarOutro}>
                Buscar outro
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-1.5">
            <p className="text-xs text-muted-foreground">Sem vínculo</p>
            <Button type="button" size="sm" variant="outline" onClick={onVincular}>
              Vincular
            </Button>
          </div>
        )}
      </TableCell>
      <TableCell className="text-right text-xs text-muted-foreground">
        {cliente.inativo && <Badge variant="destructive">Inativo no Omie</Badge>}
      </TableCell>
    </TableRow>
  );
}
